import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { defer, forkJoin, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, concatMap, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { BaseServices } from '../../kuba.services';
import { saveBlob } from '../safety-rounds/safety-round.service';
import { FlexiSafetyRoundLookupService } from '../safety-rounds/safety-round-lookup.service';
import { SafetyRoundLookupOption } from '../safety-rounds/safety-round.models';
import {
  ALL_STEPS,
  allFactors,
  completionCheck,
  departmentNames,
  departmentsWithRelevantExposure,
  ExposureCompletionCheck,
  ExposureProgress,
  exposureProgress,
  ExposureStatusFilter,
  ExposureSummaryGroup,
  guidanceParagraphs,
  HEALTH_CHECK_OPTIONS,
  healthCheckLabelKey,
  healthCheckMarker,
  includedDepartments,
  isAssessed,
  parseStep,
  pruneDepartmentIds,
  reportFileName,
  sortedDepartments,
  sortedSections,
  summaryGroups,
  toggleId,
} from './exposure-register.logic';
import {
  AssessExposureFactorRequest,
  ExposureAssessmentDepartment,
  ExposureAssessmentDetail,
  ExposureAssessmentFactor,
  ExposureAssessmentSection,
  ExposureAssessmentStatus,
  ExposureAssessmentStep,
  ExposureHealthCheck,
  SaveExposureDepartmentRequest,
  UpdateExposureAssessmentDetailsRequest,
} from './exposure-register.models';
import { FlexiExposureRegisterService } from './exposure-register.service';

/** Debounce before typed text (rationale, department exposure) is saved. */
const AUTOSAVE_DELAY_MS = 800;

type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

/** One button of the summary's status filter. */
interface StatusFilterOption {
  value: ExposureStatusFilter;
  labelKey: string;
}

/**
 * Flexi › Exposure Register › one assessment:
 * Introduction → Departments → Assessment → Summary, then Complete.
 *
 * Everything saves automatically: choices at once, typed text after a short
 * pause, and every save goes through one queue so they reach the server in
 * the order they were made. The step the user is on is stored as CurrentStep,
 * so the assessment resumes there. A completed assessment, or a user who is
 * not an author, gets everything read-only.
 */
@Component({
  selector: 'flexi-exposure-assessment',
  templateUrl: './exposure-assessment.component.html',
  styleUrls: ['./exposure-assessment.component.scss'],
  standalone: false,
})
export class ExposureAssessmentComponent implements OnInit, OnDestroy {
  readonly Step = ExposureAssessmentStep;
  readonly Status = ExposureAssessmentStatus;
  readonly steps = ALL_STEPS;
  readonly healthChecks = HEALTH_CHECK_OPTIONS;
  readonly marker = healthCheckMarker;
  readonly labelKey = healthCheckLabelKey;
  readonly statusFilters: StatusFilterOption[] = [
    { value: 'all', labelKey: 'FLEXI_EXPOSURE.SUMMARY.ALL' },
    { value: ExposureHealthCheck.Relevant, labelKey: 'FLEXI_EXPOSURE.HEALTH_CHECK.RELEVANT' },
    { value: ExposureHealthCheck.Unclear, labelKey: 'FLEXI_EXPOSURE.HEALTH_CHECK.UNCLEAR' },
    { value: ExposureHealthCheck.NotRelevant, labelKey: 'FLEXI_EXPOSURE.HEALTH_CHECK.NOT_RELEVANT' },
  ];

  assessmentId = 0;
  assessment: ExposureAssessmentDetail | null = null;
  loading = true;
  loadFailed = false;
  refreshing = 0;
  step: ExposureAssessmentStep = ExposureAssessmentStep.Introduction;

  /**
   * Admin, a listed HSE advisor of the business, or its OHS responsible — as
   * answered by the backend's permissions endpoint. False until it answers.
   */
  canAuthor = false;

  /** The business's users: the responsible person and the action point owner. */
  users: SafetyRoundLookupOption[] = [];

  /* ---- Derived from the assessment ---- */
  sections: ExposureAssessmentSection[] = [];
  departments: ExposureAssessmentDepartment[] = [];
  included: ExposureAssessmentDepartment[] = [];
  progress: ExposureProgress = { assessed: 0, total: 0, percent: 0, relevant: 0, unclear: 0, notRelevant: 0 };
  sectionProgress = new Map<string, ExposureProgress>();
  completion: ExposureCompletionCheck = { includedDepartments: 0, unassessed: 0, canComplete: false };
  relevantDepartmentCount = 0;
  /** Guidance split into paragraphs, per factor id. */
  guidance = new Map<number, string[]>();

  /* ---- Step 1: details ---- */
  title = '';
  responsibleUserId: number | null = null;
  titleInvalid = false;

  /* ---- Autosave ---- */
  saveState: SaveState = 'idle';
  savedAt: Date | null = null;

  /* ---- Step 3: assessment ---- */
  openSection: string | null = null;
  openFactorId: number | null = null;
  guideOpen = new Set<number>();
  refsOpen = new Set<number>();

  /* ---- Step 4: summary ---- */
  summaryDepartmentId: number | null = null;
  summaryStatus: ExposureStatusFilter = ExposureHealthCheck.Relevant;
  groups: ExposureSummaryGroup[] = [];
  downloading = false;
  completeOpen = false;

  private loadRequest?: Subscription;
  private usersLoaded = false;
  private departmentsTimer: Subscription | null = null;
  private readonly rationaleTimers = new Map<number, Subscription>();
  private savesInFlight = 0;
  private readonly saveQueue$ = new Subject<() => Observable<unknown>>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: FlexiExposureRegisterService,
    private lookups: FlexiSafetyRoundLookupService,
    private route: ActivatedRoute,
    private router: Router,
    private notifications: NotificationService,
  ) {}

  get businessId(): number {
    const routedBusiness = this.route.pathFromRoot?.find(route => route.routeConfig?.path === 'business/:id');
    return Number(routedBusiness?.snapshot.paramMap.get('id')) || BaseServices.BusinessId;
  }

  get isCompleted(): boolean {
    return this.assessment?.Status === ExposureAssessmentStatus.Completed;
  }

  /** Nothing on the page can be changed. */
  get readOnly(): boolean {
    return !this.canAuthor || this.isCompleted;
  }

  /** Typed text waiting for its debounce, or saves still on the wire. */
  get hasUnsavedChanges(): boolean {
    return this.departmentsTimer !== null || this.rationaleTimers.size > 0 || this.savesInFlight > 0;
  }

  get canSubmitCompletion(): boolean {
    return !this.readOnly && this.completion.canComplete && !this.hasUnsavedChanges;
  }

  /** The Action plan tab, under the current business URL prefix. */
  get actionPointsUrl(): string {
    return `${this.router.url.split('/Flexi')[0]}/Flexi/action-points`;
  }

  ngOnInit(): void {
    this.saveQueue$
      .pipe(
        concatMap(operation => operation()),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.assessmentId = Number(params.get('assessmentId'));
      this.load();
    });
  }

  ngOnDestroy(): void {
    // Text still waiting for its debounce is sent rather than lost. These
    // requests are deliberately not tied to destroy$: they are short and
    // finite, and cancelling them is exactly what would lose the text.
    if (!this.readOnly && this.assessment) {
      const pendingFactors = [...this.rationaleTimers.keys()];
      const pendingDepartments = this.departmentsTimer !== null;
      this.cancelTimers();
      if (pendingDepartments) {
        this.api.saveDepartments(this.businessId, this.assessmentId, this.departmentsRequest()).subscribe({ error: () => undefined });
      }
      for (const factorId of pendingFactors) {
        const factor = this.findFactor(factorId);
        if (factor) {
          this.api
            .assessFactor(this.businessId, this.assessmentId, factorId, this.factorRequest(factor))
            .subscribe({ error: () => undefined });
        }
      }
      if (this.step === ExposureAssessmentStep.Introduction && this.detailsDirty && this.title.trim()) {
        this.api.updateDetails(this.businessId, this.assessmentId, this.detailsRequest()).subscribe({ error: () => undefined });
      }
    } else {
      this.cancelTimers();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ======================================================================
     Loading
     ====================================================================== */

  load(): void {
    this.loadRequest?.unsubscribe();
    this.assessment = null;
    this.loading = true;
    this.loadFailed = false;
    const users$ = this.usersLoaded ? of(null) : this.lookups.businessUsers(this.businessId);
    this.loadRequest = forkJoin({
      assessment: this.api.get(this.businessId, this.assessmentId),
      permissions: this.api.permissions(this.businessId),
      users: users$,
    })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.loading = false)))
      .subscribe({
        next: ({ assessment, permissions, users }) => {
          this.canAuthor = permissions.CanAuthor;
          if (users) {
            this.users = users;
            this.usersLoaded = true;
          }
          this.applyAssessment(assessment, true);
          this.step = this.initialStep(assessment);
          this.openFirstSection();
        },
        error: () => {
          this.assessment = null;
          this.loadFailed = true;
        },
      });
  }

  /** Re-reads the assessment, keeping text the user is still typing. */
  private reload(): Observable<ExposureAssessmentDetail | null> {
    return defer(() => {
      this.refreshing++;
      return this.api.get(this.businessId, this.assessmentId);
    }).pipe(
      finalize(() => this.refreshing--),
      map(fresh => {
        this.applyAssessment(fresh, false);
        return fresh;
      }),
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    );
  }

  private applyAssessment(assessment: ExposureAssessmentDetail, resetForm: boolean): void {
    const previousFactors = allFactors(this.sections);
    const previousDepartments = this.departments;
    this.assessment = assessment;
    this.sections = sortedSections(assessment.Sections ?? []);
    for (const factor of allFactors(this.sections)) {
      factor.DepartmentIds = factor.DepartmentIds ?? [];
      factor.RegulatoryReferences = factor.RegulatoryReferences ?? [];
      // A rationale whose debounce has not fired yet is newer than the server's.
      if (!resetForm && this.rationaleTimers.has(factor.Id)) {
        factor.Rationale = previousFactors.find(previous => previous.Id === factor.Id)?.Rationale ?? factor.Rationale;
      }
    }
    // The same for department text still waiting to be saved.
    this.departments = !resetForm && this.departmentsTimer !== null && previousDepartments.length
      ? previousDepartments
      : sortedDepartments(assessment.Departments ?? []).map(department => ({ ...department }));
    if (resetForm) {
      this.title = assessment.Title;
      this.responsibleUserId = assessment.ResponsibleUserId;
      this.titleInvalid = false;
    }
    this.guidance.clear();
    allFactors(this.sections).forEach(factor => this.guidance.set(factor.Id, guidanceParagraphs(factor.Guidance)));
    this.refreshDerived();
  }

  /** A `?step=` query param wins; then the stored step; a completed assessment opens on its summary. */
  private initialStep(assessment: ExposureAssessmentDetail): ExposureAssessmentStep {
    const requested = parseStep(this.route.snapshot.queryParamMap.get('step'));
    if (requested) {
      return requested;
    }
    if (assessment.Status === ExposureAssessmentStatus.Completed) {
      return ExposureAssessmentStep.Summary;
    }
    return parseStep(String(assessment.CurrentStep)) ?? ExposureAssessmentStep.Introduction;
  }

  /** Recomputes everything derived from departments and factors. Called after every change. */
  refreshDerived(): void {
    this.included = includedDepartments(this.departments);
    const factors = allFactors(this.sections);
    this.progress = exposureProgress(factors);
    this.sectionProgress.clear();
    this.sections.forEach(section => this.sectionProgress.set(section.Name, exposureProgress(section.Factors)));
    this.completion = completionCheck(this.departments, this.sections);
    this.relevantDepartmentCount = departmentsWithRelevantExposure(this.sections, this.departments);
    if (this.summaryDepartmentId !== null && !this.included.some(d => d.DepartmentId === this.summaryDepartmentId)) {
      this.summaryDepartmentId = null;
    }
    this.updateGroups();
  }

  private findFactor(factorId: number): ExposureAssessmentFactor | undefined {
    return allFactors(this.sections).find(factor => factor.Id === factorId);
  }

  /* ======================================================================
     Steps and navigation
     ====================================================================== */

  selectStep(step: ExposureAssessmentStep): void {
    if (step === this.step) {
      return;
    }
    if (!this.readOnly && this.step === ExposureAssessmentStep.Introduction && !this.title.trim()) {
      this.titleInvalid = true;
      return;
    }
    this.titleInvalid = false;
    this.flushPending();
    this.step = step;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    window.scrollTo({ top: 0 });
    // Remember where the author is, together with any change to the header.
    if (!this.readOnly) {
      this.enqueue(() => this.sendDetails());
    }
  }

  previousStep(): void {
    if (this.step > ExposureAssessmentStep.Introduction) {
      this.selectStep((this.step - 1) as ExposureAssessmentStep);
    }
  }

  nextStep(): void {
    if (this.step < ExposureAssessmentStep.Summary) {
      this.selectStep((this.step + 1) as ExposureAssessmentStep);
    }
  }

  stepKey(step: ExposureAssessmentStep): string {
    return `FLEXI_EXPOSURE.STEP.${step}`;
  }

  backToOverview(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  /** The overview's "About the company" card, where the basis texts are edited. */
  editBasisTexts(): void {
    this.router.navigate(['../'], { relativeTo: this.route, fragment: 'fer-about' });
  }

  /* ======================================================================
     Step 1 — details
     ====================================================================== */

  private get detailsDirty(): boolean {
    const assessment = this.assessment;
    return !!assessment
      && (assessment.Title !== this.title.trim() || assessment.ResponsibleUserId !== this.responsibleUserId);
  }

  private detailsRequest(): UpdateExposureAssessmentDetailsRequest {
    return {
      Title: this.title.trim() || this.assessment?.Title || '',
      ResponsibleUserId: this.responsibleUserId,
      CurrentStep: this.step,
    };
  }

  private sendDetails(): Observable<unknown> {
    const request = this.detailsRequest();
    return this.runSave(
      () => this.api.updateDetails(this.businessId, this.assessmentId, request),
      () => {
        const assessment = this.assessment;
        if (!assessment) return;
        assessment.Title = request.Title;
        assessment.CurrentStep = request.CurrentStep;
        if (assessment.ResponsibleUserId !== request.ResponsibleUserId) {
          assessment.ResponsibleUserId = request.ResponsibleUserId;
          assessment.ResponsibleUserName = this.users.find(u => u.value === request.ResponsibleUserId)?.label ?? null;
          assessment.ResponsibleRoleText = null;
        }
      },
    );
  }

  /* ======================================================================
     Step 2 — departments
     ====================================================================== */

  toggleDepartment(department: ExposureAssessmentDepartment): void {
    if (this.readOnly) {
      return;
    }
    department.IsIncluded = !department.IsIncluded;
    if (!department.IsIncluded) {
      // The backend removes an excluded department from every factor; do the same here.
      allFactors(this.sections).forEach(factor => (factor.DepartmentIds = pruneDepartmentIds(factor.DepartmentIds, this.departments)));
    }
    this.refreshDerived();
    this.scheduleDepartments();
  }

  departmentTextChanged(): void {
    if (!this.readOnly) {
      this.scheduleDepartments();
    }
  }

  private scheduleDepartments(): void {
    this.departmentsTimer?.unsubscribe();
    this.saveState = 'idle';
    this.departmentsTimer = timer(AUTOSAVE_DELAY_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.departmentsTimer = null;
        this.enqueue(() => this.sendDepartments());
      });
  }

  private departmentsRequest(): SaveExposureDepartmentRequest[] {
    return this.departments.map(department => ({
      DepartmentId: department.DepartmentId,
      IsIncluded: department.IsIncluded,
      ExposureDescription: department.ExposureDescription?.trim() ? department.ExposureDescription : null,
    }));
  }

  private sendDepartments(): Observable<unknown> {
    return this.runSave(() => this.api.saveDepartments(this.businessId, this.assessmentId, this.departmentsRequest()));
  }

  /* ======================================================================
     Step 3 — assessment
     ====================================================================== */

  private openFirstSection(): void {
    const target = this.sections.find(section => section.Factors.some(factor => !isAssessed(factor))) ?? this.sections[0];
    this.openSection = target?.Name ?? null;
    this.openFactorId = null;
  }

  toggleSection(section: ExposureAssessmentSection): void {
    this.openSection = this.openSection === section.Name ? null : section.Name;
    this.openFactorId = null;
  }

  toggleFactor(factor: ExposureAssessmentFactor): void {
    this.openFactorId = this.openFactorId === factor.Id ? null : factor.Id;
  }

  toggleGuide(factor: ExposureAssessmentFactor): void {
    this.toggleIn(this.guideOpen, factor.Id);
  }

  toggleRefs(factor: ExposureAssessmentFactor): void {
    this.toggleIn(this.refsOpen, factor.Id);
  }

  factorDepartmentText(factor: ExposureAssessmentFactor): string {
    return departmentNames(factor.DepartmentIds, this.departments);
  }

  setHealthCheck(factor: ExposureAssessmentFactor, healthCheck: ExposureHealthCheck): void {
    if (this.readOnly || factor.HealthCheck === healthCheck) {
      return;
    }
    factor.HealthCheck = healthCheck;
    this.queueFactor(factor);
    this.refreshDerived();
  }

  toggleFactorDepartment(factor: ExposureAssessmentFactor, departmentId: number): void {
    if (this.readOnly) {
      return;
    }
    factor.DepartmentIds = toggleId(factor.DepartmentIds, departmentId);
    this.queueFactor(factor);
    this.refreshDerived();
  }

  /** Debounced: the save fires once typing pauses. */
  rationaleChanged(factor: ExposureAssessmentFactor): void {
    if (this.readOnly) {
      return;
    }
    this.rationaleTimers.get(factor.Id)?.unsubscribe();
    this.saveState = 'idle';
    this.rationaleTimers.set(
      factor.Id,
      timer(AUTOSAVE_DELAY_MS)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.rationaleTimers.delete(factor.Id);
          this.enqueue(() => this.sendFactor(factor.Id));
        }),
    );
  }

  /** Saves at once, taking any rationale still waiting for its debounce along. */
  private queueFactor(factor: ExposureAssessmentFactor): void {
    this.rationaleTimers.get(factor.Id)?.unsubscribe();
    this.rationaleTimers.delete(factor.Id);
    this.enqueue(() => this.sendFactor(factor.Id));
  }

  private factorRequest(factor: ExposureAssessmentFactor): AssessExposureFactorRequest {
    return {
      HealthCheck: factor.HealthCheck,
      Rationale: factor.Rationale?.trim() ? factor.Rationale : null,
      DepartmentIds: pruneDepartmentIds(factor.DepartmentIds, this.departments),
    };
  }

  /** The factor as it is NOW, so an edit made while an earlier save was on the wire is never lost. */
  private sendFactor(factorId: number): Observable<unknown> {
    const factor = this.findFactor(factorId);
    if (!factor) {
      return of(null);
    }
    return this.runSave(() => this.api.assessFactor(this.businessId, this.assessmentId, factorId, this.factorRequest(factor)));
  }

  goToFirstUnassessed(): void {
    const section = this.sections.find(s => s.Factors.some(factor => !isAssessed(factor)));
    this.selectStep(ExposureAssessmentStep.Assessment);
    if (section) {
      this.openSection = section.Name;
      this.openFactorId = section.Factors.find(factor => !isAssessed(factor))?.Id ?? null;
    }
  }

  /* ======================================================================
     Step 4 — summary and completion
     ====================================================================== */

  pickStatus(status: ExposureStatusFilter): void {
    this.summaryStatus = status;
    this.updateGroups();
  }

  summaryDepartmentChanged(value: number | string | null): void {
    const id = value === null || value === '' ? null : Number(value);
    this.summaryDepartmentId = id;
    this.updateGroups();
  }

  private updateGroups(): void {
    this.groups = summaryGroups(this.sections, this.summaryDepartmentId, this.summaryStatus);
  }

  /** The archived report once completed, a preview generated on the fly before that. */
  downloadReport(): void {
    const assessment = this.assessment;
    if (!assessment || this.downloading) {
      return;
    }
    this.flushPending();
    this.downloading = true;
    this.api
      .downloadReport(this.businessId, assessment.Id)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.downloading = false)))
      .subscribe({
        next: file => saveBlob(file.blob, file.fileName ?? reportFileName(assessment.Title)),
        // The shared HTTP interceptor reports the failure; finalize enables a retry.
        error: () => undefined,
      });
  }

  openComplete(): void {
    if (this.canSubmitCompletion) {
      this.completeOpen = true;
    }
  }

  /** The dialog completed the assessment: show it read-only from now on. */
  completed(): void {
    this.reload().subscribe(fresh => {
      if (fresh) {
        this.step = ExposureAssessmentStep.Summary;
      }
    });
  }

  completeClosed(): void {
    this.completeOpen = false;
  }

  /** Why "Complete assessment" is blocked, or null. */
  get completeBlockedKey(): string | null {
    if (this.readOnly) {
      return null;
    }
    if (this.completion.includedDepartments === 0) {
      return 'FLEXI_EXPOSURE.SUMMARY.BLOCKED_DEPARTMENTS';
    }
    if (this.completion.unassessed > 0) {
      return 'FLEXI_EXPOSURE.SUMMARY.BLOCKED_FACTORS';
    }
    if (this.hasUnsavedChanges) {
      return 'FLEXI_EXPOSURE.SAVING';
    }
    return null;
  }

  /* ======================================================================
     Save queue
     ====================================================================== */

  private enqueue(operation: () => Observable<unknown>): void {
    this.saveQueue$.next(operation);
  }

  /** Sends everything still waiting for its debounce. */
  private flushPending(): void {
    if (this.departmentsTimer !== null) {
      this.departmentsTimer.unsubscribe();
      this.departmentsTimer = null;
      this.enqueue(() => this.sendDepartments());
    }
    const pending = [...this.rationaleTimers.keys()];
    pending.forEach(factorId => {
      this.rationaleTimers.get(factorId)?.unsubscribe();
      this.rationaleTimers.delete(factorId);
      this.enqueue(() => this.sendFactor(factorId));
    });
  }

  private cancelTimers(): void {
    this.departmentsTimer?.unsubscribe();
    this.departmentsTimer = null;
    this.rationaleTimers.forEach(sub => sub.unsubscribe());
    this.rationaleTimers.clear();
  }

  /**
   * One queued save. A failure is reported here (the request skips the global
   * toast) and the assessment is re-read, so the page shows what the server
   * actually holds — a 409 means it was completed elsewhere.
   */
  private runSave(send: () => Observable<void>, onSuccess?: () => void): Observable<unknown> {
    if (this.readOnly) {
      return of(null);
    }
    return defer(() => {
      this.savesInFlight++;
      this.saveState = 'saving';
      return send().pipe(
        tap(() => {
          onSuccess?.();
          this.saveState = 'saved';
          this.savedAt = new Date();
        }),
        catchError(() => {
          this.saveState = 'failed';
          this.notifications.error('FLEXI_EXPOSURE.FEEDBACK.SAVE_FAILED');
          return this.reload();
        }),
        finalize(() => {
          this.savesInFlight--;
          this.completion = completionCheck(this.departments, this.sections);
        }),
      );
    });
  }

  private toggleIn(set: Set<number>, id: number): void {
    if (set.has(id)) {
      set.delete(id);
    } else {
      set.add(id);
    }
  }
}
